import { motion } from "framer-motion";

const chapters = [
  {
    no: "01",
    title: "THE DESCENT",
    body: "Somewhere beneath the surface, a signal never stops broadcasting. THINKAVARIUM is the channel documenting what the world above chose to forget — experiments, transmissions, and stories from the Vault.",
  },
  {
    no: "02",
    title: "THE TRANSMISSION",
    body: "Every episode is a new dispatch: builds, deep dives, and long-form investigations. New signals surface weekly, and once they're out, they're impossible to catch up on without the frequency.",
  },
  {
    no: "03",
    title: "THE FREQUENCY",
    body: "The mailing list is the frequency. Enlist and you receive each episode the moment it drops — no algorithm, no missed uploads, no static. Just a direct line to the deep.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function About() {
  return (
    <section id="about" className="relative bg-bunker-shadow px-6 py-28 md:px-10 md:py-40" data-testid="about">
      <div className="mx-auto max-w-[1400px]">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          custom={0}
          className="mb-16 flex items-end justify-between border-b border-white/10 pb-6"
        >
          <div>
            <span className="font-mono text-xs tracking-[0.3em] text-reactor-red md:text-[15px]">// FIELD BRIEFING</span>
            <h2 className="mt-3 font-display text-5xl uppercase leading-none text-white md:text-7xl">
              The Project
            </h2>
          </div>
          <span className="hidden font-mono text-xs tracking-[0.3em] text-white/30 md:block md:text-[15px]">
            DOC. V-78 / CLASSIFIED
          </span>
        </motion.div>

        <div className="grid gap-px bg-white/10 md:grid-cols-3">
          {chapters.map((c, i) => (
            <motion.article
              key={c.no}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i + 1}
              className="group relative bg-bunker-shadow p-8 transition-colors duration-300 hover:bg-gunmetal md:p-10"
              data-testid={`chapter-${c.no}`}
            >
              <div className="mb-8 flex items-baseline gap-4">
                <span className="font-display text-6xl leading-none text-reactor-red text-glow-red md:text-7xl">
                  {c.no}
                </span>
                <span className="h-px flex-1 bg-white/15 transition-colors duration-300 group-hover:bg-hazard-amber" />
              </div>
              <h3 className="mb-4 font-mono text-lg uppercase tracking-widest text-hazard-amber md:text-[22px]">
                {c.title}
              </h3>
              <p className="font-body text-sm font-light leading-relaxed text-white/60 md:text-[18px]">
                {c.body}
              </p>
            </motion.article>
          ))}
        </div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          custom={4}
          className="mx-auto mt-16 max-w-4xl text-center"
          data-testid="about-dossier"
        >
          <p className="font-body text-base font-light leading-relaxed text-white/80 md:text-xl">
            Hosted by a former intelligence professional and technical engineer, Thinkavarium
            reverse-engineers sanitized, declassified government documents and investigates the
            mysteries the establishment wants you to forget. We bridge the gap between classified
            files and public knowledge.
          </p>
          <p className="mt-20 font-mono text-xs font-light leading-relaxed text-white/50 md:text-sm">
            Thinkavarium is filmed in a practical set. No green screens, no AI presenters, and no AI
            voices &mdash; just real, unfiltered analysis. If your frequency is already tuned to the
            long-form investigative styles of{" "}
            <span className="text-hazard-amber/80">
              The Why Files, Lemmino, Thoughty2, Barely Sociable, or Kergazart
            </span>{" "}
            &mdash; you have found your new operational base.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
